import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Brain } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { quranApi, memorizationApi } from '../services/api';
import { quranAudioProvider } from '../audio/quranAudioProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { SectionHeader, PageLoading, GlassCard } from '../components/shared';
import { toast } from 'sonner';

export default function MemorizationCreate() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [scopeType, setScopeType] = useState('ayah_range');
  const [surahNumber, setSurahNumber] = useState(112);
  const [fromAyah, setFromAyah] = useState(1);
  const [toAyah, setToAyah] = useState(4);
  const [reciterId, setReciterId] = useState('alafasy');
  const [title, setTitle] = useState('');

  const reciters = useMemo(() => quranAudioProvider.getAvailableReciters(), []);

  useEffect(() => {
    quranApi.getSurahs().then((r) => {
      setSurahs(r.data || []);
      setLoading(false);
    }).catch(() => {
      toast.error(t('common.error_generic'));
      setLoading(false);
    });
  }, []);

  const selectedSurah = surahs.find((s) => s.number === Number(surahNumber));
  const maxAyah = selectedSurah?.verses_count || 7;

  useEffect(() => {
    // Clamp range when surah changes
    if (fromAyah > maxAyah) setFromAyah(1);
    if (toAyah > maxAyah) setToAyah(maxAyah);
  }, [maxAyah, fromAyah, toAyah]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim() || undefined,
        scope: {
          type: scopeType,
          surahNumber: Number(surahNumber),
          ...(scopeType === 'ayah_range'
            ? { fromAyah: Number(fromAyah), toAyah: Number(toAyah) }
            : {}),
        },
        reciter_id: reciterId,
        riwaya: 'hafs',
      };
      const res = await memorizationApi.createPlan(payload);
      toast.success(t('common.saved'));
      navigate(`/memorization/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) return <PageLoading />;
  if (!user) return <Navigate to="/login?next=/memorization/new" replace />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="memo-new">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/memorization')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="memo-new-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('common.back')}
      </Button>

      <SectionHeader
        overline={t('memorization.title')}
        title={t('memorization.new_plan')}
        subtitle={t('memorization.empty_subtitle')}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <GlassCard>
          <Label className="text-sm text-text-secondary mb-3 block">{t('memorization.scope')}</Label>
          <RadioGroup
            value={scopeType}
            onValueChange={setScopeType}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="ayah_range" id="scope-range" data-testid="scope-range-radio" />
              <Label htmlFor="scope-range" className="cursor-pointer">{t('memorization.scope_range')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="full_surah" id="scope-full" data-testid="scope-full-radio" />
              <Label htmlFor="scope-full" className="cursor-pointer">{t('memorization.scope_full')}</Label>
            </div>
          </RadioGroup>
        </GlassCard>

        <GlassCard className="space-y-4">
          <div>
            <Label className="text-sm text-text-secondary mb-2 block">{t('memorization.surah')}</Label>
            <Select
              value={String(surahNumber)}
              onValueChange={(v) => setSurahNumber(Number(v))}
            >
              <SelectTrigger className="bg-background border-border" data-testid="memo-surah-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 bg-surface border-border">
                {surahs.map((s) => (
                  <SelectItem key={s.number} value={String(s.number)} data-testid={`surah-option-${s.number}`}>
                    {s.number}. {s.name_french || s.name_english} — {s.name_arabic} ({s.verses_count} versets)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {scopeType === 'ayah_range' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-text-secondary mb-2 block">{t('memorization.from_ayah')}</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxAyah}
                  value={fromAyah}
                  onChange={(e) => setFromAyah(Number(e.target.value))}
                  className="bg-background border-border"
                  data-testid="memo-from-ayah"
                />
              </div>
              <div>
                <Label className="text-sm text-text-secondary mb-2 block">{t('memorization.to_ayah')}</Label>
                <Input
                  type="number"
                  min={fromAyah}
                  max={maxAyah}
                  value={toAyah}
                  onChange={(e) => setToAyah(Number(e.target.value))}
                  className="bg-background border-border"
                  data-testid="memo-to-ayah"
                />
              </div>
            </div>
          )}

          <p className="text-xs text-text-muted">
            {scopeType === 'full_surah'
              ? `${t('memorization.scope_full')} : ${maxAyah}`
              : `${t('memorization.scope_range')} : ${Math.max(0, toAyah - fromAyah + 1)}`}
          </p>
        </GlassCard>

        <GlassCard className="space-y-4">
          <div>
            <Label className="text-sm text-text-secondary mb-2 block">{t('memorization.reciter')}</Label>
            <Select value={reciterId} onValueChange={setReciterId}>
              <SelectTrigger className="bg-background border-border" data-testid="memo-reciter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border">
                {reciters.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-text-muted mt-2">
              {t('memorization.reciter_locked')}
            </p>
          </div>

          <div>
            <Label className="text-sm text-text-secondary mb-2 block">{t('memorization.title_optional')}</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('memorization.title_placeholder')}
              className="bg-background border-border"
              data-testid="memo-title-input"
            />
          </div>
        </GlassCard>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary hover:bg-primary/90 text-background"
          data-testid="memo-create-submit"
        >
          {submitting ? t('memorization.creating') : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              {t('memorization.create_plan')}
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
